import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REPORT_TYPE_IDS, reportTypesV1 } from '../consts/v1/reportTypes';
import {
  SECTION_1_TABLES as SECTION_1_TABLES_V1,
  SECTION_2_TABLES as SECTION_2_TABLES_V1,
  SECTION_3_TABLES as SECTION_3_TABLES_V1,
  SECTION_4_TABLES as SECTION_4_TABLES_V1
} from '../consts/v1/tables';

export const useReportSections = ({ currentReportType, managerRequiredSections, funds }) => {
  const reportVersion = 'v1';

  const versionedSections = useMemo(() => {
    const sections = reportTypesV1.find(item => item.id === currentReportType)?.sections;
    let filteredSections;
    if (currentReportType === REPORT_TYPE_IDS.AIF001 && managerRequiredSections) {
      filteredSections = (sections || []).filter(
        s => !s.key || managerRequiredSections.includes(s.key)
      );
    } else if (currentReportType === REPORT_TYPE_IDS.AIF002) {
      const fundsList = Object.values(funds || {});
      const anyFundHasSections = fundsList.some(f => Array.isArray(f.required_sections));
      if (!anyFundHasSections) {
        filteredSections = sections || [];
      } else {
        filteredSections = (sections || []).filter(s => {
          if (!s.key) return true;
          if (!fundsList.length) return true;
          return fundsList.some(
            fund => Array.isArray(fund.required_sections) && fund.required_sections.includes(s.key)
          );
        });
      }
    } else {
      filteredSections = sections || [];
    }
    return {
      sections,
      filteredSections
    };
  }, [currentReportType, managerRequiredSections, funds]);

  const sectionTablesByVersion = useMemo(
    () =>
      reportVersion === 'v1'
        ? {
            ...SECTION_1_TABLES_V1,
            ...SECTION_2_TABLES_V1,
            ...SECTION_3_TABLES_V1,
            ...SECTION_4_TABLES_V1
          }
        : {
            1: SECTION_1_TABLES_V1,
            2: SECTION_2_TABLES_V1,
            3: SECTION_3_TABLES_V1,
            4: SECTION_4_TABLES_V1
          },
    []
  );

  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const virtuosoRef = useRef();
  const initializedFirstSectionKeyRef = useRef(null);

  const onToggleSection = useCallback(key => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onToggleQuestion = useCallback((sectionKey, qIndex) => {
    const qKey = `${sectionKey}::q-${qIndex}`;

    setExpandedSections(prev => {
      if (prev.has(sectionKey)) return prev;
      return new Set([...prev, sectionKey]);
    });

    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(qKey)) next.delete(qKey);
      else next.add(qKey);
      return next;
    });
  }, []);

  useEffect(() => {
    const sections = versionedSections.filteredSections;
    if (!sections?.length) return;

    const firstSection = sections[0];
    const firstKey = firstSection.key || firstSection.name;

    if (initializedFirstSectionKeyRef.current === firstKey) return;
    initializedFirstSectionKeyRef.current = firstKey;

    setExpandedSections(prev => {
      if (prev.has(firstKey)) return prev;
      return new Set([...prev, firstKey]);
    });

    const questionsByName = new Map();
    (firstSection.questions || []).forEach(q => {
      const name = q?.name || 'Question';
      if (!questionsByName.has(name)) {
        questionsByName.set(name, true);
      }
    });

    const firstSectionQuestionKeys = Array.from(questionsByName.keys()).map(
      (_, index) => `${firstKey}::q-${index}`
    );

    setExpandedQuestions(prev => {
      const hasAllFirstSectionQuestions = firstSectionQuestionKeys.every(questionKey =>
        prev.has(questionKey)
      );
      if (hasAllFirstSectionQuestions) return prev;

      const next = new Set(prev);
      firstSectionQuestionKeys.forEach(questionKey => {
        next.add(questionKey);
      });
      return next;
    });
  }, [versionedSections.filteredSections]);

  const onNavigate = useCallback(
    ({ sectionKey, questionIndex, shouldExpand }) => {
      const sections = versionedSections.filteredSections || [];

      if (shouldExpand) {
        if (sectionKey) {
          setExpandedSections(prev => new Set([...prev, sectionKey]));
        }
        if (sectionKey && typeof questionIndex === 'number') {
          const qKey = `${sectionKey}::q-${questionIndex}`;
          setExpandedQuestions(prev => new Set([...prev, qKey]));
        }
      }

      let index = 0;
      for (const section of sections) {
        const skey = section.key || section.name;
        const isTargetSection = skey === sectionKey;

        let questionsCount = 0;
        const isSectionExpanded = expandedSections.has(skey) || (shouldExpand && isTargetSection);

        if (isSectionExpanded) {
          const questionsByName = new Map();
          (section.questions || []).forEach(q => {
            const name = q?.name || 'Question';
            if (!questionsByName.has(name)) {
              questionsByName.set(name, { name, tableNames: [] });
            }
            questionsByName.get(name).tableNames.push(q.tableName);
          });
          questionsCount = questionsByName.size;
        }

        if (typeof questionIndex !== 'number' && isTargetSection) {
          setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({ index, align: 'start', behavior: 'smooth' });
          }, 50);
          return;
        }

        index += 1;

        if (
          isSectionExpanded &&
          isTargetSection &&
          typeof questionIndex === 'number' &&
          questionIndex < questionsCount
        ) {
          const targetIndex = index + questionIndex;
          setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({
              index: targetIndex,
              align: 'start',
              behavior: 'smooth'
            });
          }, 50);
          return;
        }

        index += isSectionExpanded ? questionsCount : 0;
      }
    },
    [versionedSections.filteredSections, expandedSections]
  );

  return {
    versionedSections,
    sectionTablesByVersion,
    expandedSections,
    expandedQuestions,
    onToggleSection,
    onToggleQuestion,
    virtuosoRef,
    onNavigate
  };
};
